import React from 'react';
import { Link } from 'react-router-dom';
import {
  List,
  Show,
  useListContext,
  useShowContext,
  useRefresh,
  useDataProvider,
  useGetList,
} from 'react-admin';
import { useBreadcrumbContext } from '../components/layout/AppBreadcrumb';
import {
  Calendar,
  Clock,
  Users,
  MapPin,
  User,
  Phone,
  Mail,
  MessageCircle,
  Ban,
  Trash2,
  Dumbbell,
  RotateCcw,
} from 'lucide-react';
import { SearchIntegration } from '../components/common/SearchIntegration';
import { ReactAdminPagination } from '../components/ui/react-admin-pagination';
import { ListScreen } from '../components/ui/list-screen';
import { StatCard, StatRow } from '../components/ui/stat-card';
import { SegmentedTabs } from '../components/ui/segmented-tabs';
import { DataTable, DataColumn } from '../components/ui/data-table';
import { SortMenu } from '../components/ui/sort-menu';
import { Filters, FilterTokens, FilterFieldConfig } from '../components/ui/filter-controls';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import {
  AvatarName,
  Avatar,
  StatusBadge,
  SportTag,
  Money,
  Capacity,
  RowActions,
  hueFromString,
} from '../components/ui/data-cells';
import { toastSuccess, toastError } from '../lib/toast';
import { cn } from '../lib/utils';
import { CustomDataProvider } from '../types/dataProvider';
import { logger, LogCategory } from '../utils/logger';
import { RemoveFromClassAction, canRemoveFromClass } from '../components/admin/RemoveFromClassAction';

/* ============================================================
   Sessions (Classes) — Latio design system (DESIGN_SYSTEM.md §6/§7).
   List screen + full-page detail. Preserves the session_type / status model,
   roster, capacity, chat moderation and the cancelSession action.
   ============================================================ */

interface SessionListItem {
  id: number;
  session_type: 'group_class' | 'private_session' | 'open_play';
  status: 'open' | 'full' | 'completed' | 'cancelled' | 'payment_expired';
  title?: string;
  description?: string;
  scheduled_at: string;
  duration_minutes: number;
  max_participants?: number;
  current_participants: number;
  price_per_person?: number;
  total_revenue: number;
  trainer?: { id: number; name: string; email: string; profile_picture_url?: string };
  sport?: { id: number; name: string };
  location?: { id: number; name: string; address?: string };
  created_at: string;
}

interface SessionParticipant {
  booking_id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  booking_status: string;
  joined_at: string;
}

interface SessionDetail extends SessionListItem {
  participants: SessionParticipant[];
  booking_stats: {
    total_bookings: number;
    active_bookings: number;
    cancelled_bookings: number;
    removed_bookings: number;
  };
  message_count: number;
  cancellation_reason?: string;
  cancelled_at?: string;
  requester?: { id: number; name: string; email: string; phone?: string };
  trainer_notes?: string;
}

interface SessionChatMessage {
  id: number;
  session_id: number;
  user_id?: number;
  trainer_id?: number;
  user_name?: string;
  user_username?: string;
  user_profile_picture_url?: string;
  message_content: string;
  message_type: 'text' | 'system' | 'announcement';
  created_at: string;
  is_deleted: boolean;
}

const TYPE_LABEL: Record<string, string> = {
  group_class: 'Group class',
  private_session: 'Private session',
  open_play: 'Open play',
};
const typeLabel = (t?: string) => (t ? TYPE_LABEL[t] || t.replace(/_/g, ' ') : '—');

const fmtDate = (v?: string | null) =>
  v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '—';
const fmtTime = (v?: string | null) =>
  v ? new Date(v).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '';

/* ---- Cover thumbnail (colored tile fallback; real <img> slots in) ---- */
function SessionThumb({ session, size = 40 }: { session: { title?: string; sport?: { name?: string }; session_type?: string }; size?: number }) {
  const seed = session.sport?.name || session.title || session.session_type || 'Session';
  const hue = hueFromString(seed);
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-latio-sm"
      style={{ width: size, height: size, backgroundColor: `hsl(${hue} 60% 92%)`, color: `hsl(${hue} 55% 38%)` }}
      aria-hidden
    >
      <Dumbbell className="size-1/2" />
    </span>
  );
}

/* ---- List config ---- */
const SESSION_TABS = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'full', label: 'Full' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];
const SESSION_FILTERS: FilterFieldConfig[] = [
  {
    field: 'session_type',
    label: 'Type',
    options: [
      { value: 'group_class', label: 'Group class' },
      { value: 'private_session', label: 'Private session' },
      { value: 'open_play', label: 'Open play' },
    ],
  },
];
const SESSION_SORT = [
  { field: 'date_time', label: 'Schedule' },
  { field: 'total_revenue', label: 'Revenue' },
  { field: 'current_participants', label: 'Capacity' },
  { field: 'status', label: 'Status' },
  { field: 'id', label: 'ID' },
];
const SESSION_SORT_LABELS = Object.fromEntries(SESSION_SORT.map((f) => [f.field, f.label]));

const SessionStatusTabs = () => {
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const current = (filterValues.status as string) || 'all';
  return (
    <SegmentedTabs
      tabs={SESSION_TABS}
      value={current}
      onChange={(v) => setFilters({ ...filterValues, status: v === 'all' ? undefined : v }, displayedFilters)}
    />
  );
};

const SessionStats = () => {
  const base = { pagination: { page: 1, perPage: 1 }, sort: { field: 'date_time', order: 'DESC' } } as const;
  const all = useGetList('sessions', { ...base, filter: {} });
  const open = useGetList('sessions', { ...base, filter: { status: 'open' } });
  const full = useGetList('sessions', { ...base, filter: { status: 'full' } });
  const completed = useGetList('sessions', { ...base, filter: { status: 'completed' } });
  const n = (v?: number) => (v === undefined ? '—' : v);
  return (
    <StatRow>
      <StatCard primary label="Total classes" value={n(all.total)} />
      <StatCard label="Open" value={n(open.total)} />
      <StatCard label="Full" value={n(full.total)} />
      <StatCard label="Completed" value={n(completed.total)} />
    </StatRow>
  );
};

/* ---- Inline cancel (open/full only) ---- */
function InlineCancel({ session }: { session: SessionListItem }) {
  const refresh = useRefresh();
  const dataProvider = useDataProvider<CustomDataProvider>();
  const [open, setOpen] = React.useState(false);
  if (session.status !== 'open' && session.status !== 'full') return null;
  const cancel = async (reason?: string) => {
    try {
      await dataProvider.cancelSession({ id: session.id, reason: reason || 'Cancelled by admin', notify_participants: true });
      toastSuccess('Class cancelled');
      refresh();
    } catch {
      toastError('Failed to cancel class');
    }
  };
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Cancel class"
        className="inline-flex size-8 items-center justify-center rounded-pill text-ink-2 hover:bg-error-bg hover:text-error"
      >
        <Ban className="size-4" />
      </button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        tone="destructive"
        title={`Cancel class #${session.id}?`}
        consequence="This cancels the class and all active bookings. Participants are notified."
        confirmLabel="Cancel class"
        cancelLabel="Keep class"
        reasonRequired
        reasonChips={['Trainer unavailable', 'Weather conditions', 'Facility issue', 'Low enrollment']}
        onConfirm={cancel}
      />
    </>
  );
}

/* ---- Inline reactivate (payment_expired private sessions only) ---- */
function InlineReactivateHold({ session }: { session: SessionListItem }) {
  const refresh = useRefresh();
  const dataProvider = useDataProvider<CustomDataProvider>();
  const [open, setOpen] = React.useState(false);
  if (session.status !== 'payment_expired') return null;
  const reactivate = async () => {
    try {
      await dataProvider.reactivateSessionHold({ id: session.id });
      toastSuccess('Hold reactivated - the consumer has a fresh window to pay');
      refresh();
    } catch (e: any) {
      toastError(e?.message || 'Failed to reactivate hold');
    }
  };
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Reactivate hold"
        className="inline-flex size-8 items-center justify-center rounded-pill text-ink-2 hover:bg-indigo-tint hover:text-indigo"
      >
        <RotateCcw className="size-4" />
      </button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        tone="indigo"
        title={`Reactivate hold on session #${session.id}?`}
        consequence="Reverts the booking to enquiry_accepted / pending and restarts its payment window. Use this when the hold expired while a slower payment (e.g. cash) was still in progress."
        confirmLabel="Reactivate"
        cancelLabel="Cancel"
        onConfirm={reactivate}
      />
    </>
  );
}

const SessionsListBody = () => {
  const columns: DataColumn<SessionListItem>[] = [
    { key: 'id', header: 'ID', sortable: true, width: '1px', cell: (r) => <span className="tabular-nums text-ink-2">#{r.id}</span> },
    {
      key: 'class',
      header: 'Class',
      cell: (r) => (
        <div className="flex items-center gap-3">
          <SessionThumb session={r} />
          <div className="min-w-0">
            <div className="truncate font-semibold text-ink">{r.title || typeLabel(r.session_type)}</div>
            <div className="text-[13px] text-ink-2">{r.sport?.name || 'No sport'}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'trainer',
      header: 'Coach',
      cell: (r) => (r.trainer ? <AvatarName src={r.trainer.profile_picture_url} name={r.trainer.name} size={28} /> : <span className="text-ink-3">Unassigned</span>),
    },
    { key: 'type', header: 'Type', cell: (r) => <span className="text-ink-2">{typeLabel(r.session_type)}</span> },
    {
      key: 'capacity',
      header: 'Capacity',
      sortable: true,
      sortField: 'current_participants',
      cell: (r) => <Capacity filled={r.current_participants || 0} total={r.max_participants || 0} />,
    },
    {
      key: 'schedule',
      header: 'Schedule',
      sortable: true,
      sortField: 'date_time',
      cell: (r) =>
        r.scheduled_at ? (
          <div>
            <div className="text-ink">{fmtDate(r.scheduled_at)}</div>
            <div className="text-[13px] text-ink-2">{fmtTime(r.scheduled_at)}</div>
          </div>
        ) : (
          <span className="text-ink-3">Not scheduled</span>
        ),
    },
    { key: 'total_revenue', header: 'Revenue', sortable: true, align: 'right', cell: (r) => <Money value={r.total_revenue} /> },
    { key: 'status', header: 'Status', sortable: true, cell: (r) => <StatusBadge status={r.status} /> },
    {
      key: 'actions',
      header: '',
      cell: (r) => (
        <RowActions>
          <InlineCancel session={r} />
          <InlineReactivateHold session={r} />
        </RowActions>
      ),
    },
  ];
  return (
    <ListScreen
      stats={<SessionStats />}
      tabs={<SessionStatusTabs />}
      actions={
        <>
          <Filters fields={SESSION_FILTERS} />
          <SortMenu fields={SESSION_SORT} />
        </>
      }
      tokens={<FilterTokens fields={SESSION_FILTERS} sortLabels={SESSION_SORT_LABELS} />}
    >
      <DataTable columns={columns} rowClick="show" emptyMessage="No classes found." />
      <div className="pt-1">
        <ReactAdminPagination />
      </div>
    </ListScreen>
  );
};

export const SessionList = (props: any) => (
  <List {...props} actions={false} empty={false} pagination={false} sort={{ field: 'date_time', order: 'DESC' }} perPage={25} component="div">
    <SearchIntegration filterKey="q" />
    <SessionsListBody />
  </List>
);

/* ---- Detail building blocks ---- */
function DetailCard({ title, icon: Icon, children, className }: { title?: string; icon?: any; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('rounded-card border border-hairline bg-background p-5', className)}>
      {title && (
        <h3 className="mb-3 flex items-center gap-2 text-[15px] font-bold text-ink">
          {Icon && <Icon className="size-4 text-ink-2" />}
          {title}
        </h3>
      )}
      {children}
    </div>
  );
}
function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-[13px] text-ink-2">{label}</span>
      <span className="text-right text-sm font-medium text-ink">{value}</span>
    </div>
  );
}

/* ---- Chat message ---- */
function ChatMessageRow({ message, onDelete }: { message: SessionChatMessage; onDelete: (id: number) => void }) {
  const isSystem = message.message_type === 'system';
  const isAnnouncement = message.message_type === 'announcement';
  if (isSystem || isAnnouncement) {
    return (
      <div className="my-3 flex justify-center">
        <span
          className={cn(
            'rounded-pill px-3 py-1 text-[13px]',
            isAnnouncement ? 'bg-indigo-tint text-indigo' : 'bg-surface-2 text-ink-2',
          )}
        >
          {message.is_deleted ? 'This message was deleted' : message.message_content}
        </span>
      </div>
    );
  }
  const name = message.user_username || message.user_name || 'Unknown';
  return (
    <div className="group flex gap-3 py-2">
      <Avatar src={message.user_profile_picture_url} name={name} size={32} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-ink">{name}</span>
          <span className="text-[12px] text-ink-3">{new Date(message.created_at).toLocaleString()}</span>
          {message.is_deleted && <Badge variant="secondary" className="text-[11px]">Deleted</Badge>}
        </div>
        <p className={cn('mt-0.5 text-sm', message.is_deleted ? 'italic text-ink-3' : 'text-ink')}>
          {message.is_deleted ? 'This message was deleted' : message.message_content}
        </p>
      </div>
      {!message.is_deleted && (
        <button
          type="button"
          onClick={() => onDelete(message.id)}
          title="Delete message"
          className="invisible inline-flex size-7 shrink-0 items-center justify-center rounded-pill text-ink-2 hover:bg-error-bg hover:text-error group-hover:visible"
        >
          <Trash2 className="size-3.5" />
        </button>
      )}
    </div>
  );
}

/* ---- Detail ---- */
const SessionShowContent = () => {
  const { record, isLoading } = useShowContext<SessionDetail>();
  const refresh = useRefresh();
  const dataProvider = useDataProvider<CustomDataProvider>();
  const { setData } = useBreadcrumbContext();

  const [cancelOpen, setCancelOpen] = React.useState(false);
  const [reactivateOpen, setReactivateOpen] = React.useState(false);
  const [messages, setMessages] = React.useState<SessionChatMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = React.useState(true);
  const [deleteMessageId, setDeleteMessageId] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (record) {
      setData({
        resourceId: record.id.toString(),
        resourceName: record.title || `${typeLabel(record.session_type)} #${record.id}`,
        resourceType: 'session',
      });
    }
  }, [record, setData]);

  const loadMessages = React.useCallback(async () => {
    if (!record?.id) return;
    setMessagesLoading(true);
    try {
      const { data } = await dataProvider.getSessionChatMessages({ sessionId: record.id });
      setMessages(data?.messages || []);
    } catch (error) {
      logger.error(LogCategory.API, 'Failed to fetch session messages', error, 'Sessions');
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  }, [record?.id, dataProvider]);

  React.useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  if (isLoading) return <div className="p-6 text-center text-ink-2">Loading class…</div>;
  if (!record) return <div className="p-6 text-center text-ink-2">Class not found</div>;

  const canCancel = record.status === 'open' || record.status === 'full';
  const canReactivate = record.status === 'payment_expired';

  const handleCancel = async (reason?: string) => {
    try {
      await dataProvider.cancelSession({ id: record.id, reason: reason || 'Cancelled by admin', notify_participants: true });
      toastSuccess('Class cancelled');
      refresh();
    } catch {
      toastError('Failed to cancel class');
    }
  };

  const handleReactivate = async () => {
    try {
      await dataProvider.reactivateSessionHold({ id: record.id });
      toastSuccess('Hold reactivated - the consumer has a fresh window to pay');
      refresh();
    } catch (e: any) {
      toastError(e?.message || 'Failed to reactivate hold');
    }
  };

  const handleDeleteMessage = async (reason?: string) => {
    if (deleteMessageId === null) return;
    try {
      await dataProvider.actionSessionChatMessage({ messageId: deleteMessageId, action: 'delete', reason });
      toastSuccess('Message deleted');
      loadMessages();
    } catch {
      toastError('Failed to delete message');
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Record header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <SessionThumb session={record} size={60} />
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-[24px] font-bold leading-none text-ink" style={{ letterSpacing: '-0.02em' }}>
                {record.title || `${typeLabel(record.session_type)} #${record.id}`}
              </h1>
              <StatusBadge status={record.status} />
            </div>
            <p className="mt-1 text-sm text-ink-2">
              {typeLabel(record.session_type)}
              {record.sport?.name && ` · ${record.sport.name}`}
              {record.scheduled_at && ` · ${fmtDate(record.scheduled_at)} ${fmtTime(record.scheduled_at)}`}
            </p>
          </div>
        </div>
        {canCancel && (
          <button
            onClick={() => setCancelOpen(true)}
            className="inline-flex h-9 items-center gap-2 rounded-pill bg-error px-4 text-sm font-semibold text-white hover:bg-error/90"
          >
            <Ban className="size-4" />
            Cancel class
          </button>
        )}
        {canReactivate && (
          <button
            onClick={() => setReactivateOpen(true)}
            className="inline-flex h-9 items-center gap-2 rounded-pill bg-indigo px-4 text-sm font-semibold text-white hover:bg-indigo-press"
          >
            <RotateCcw className="size-4" />
            Reactivate hold
          </button>
        )}
      </div>

      {record.status === 'cancelled' && (
        <div className="rounded-card border border-error/30 bg-error-bg px-4 py-3 text-sm text-error">
          <strong>This class was cancelled.</strong>
          {record.cancellation_reason && <span> {record.cancellation_reason}</span>}
          {record.cancelled_at && <span className="text-error/80"> · {new Date(record.cancelled_at).toLocaleString()}</span>}
        </div>
      )}

      {canReactivate && (
        <div className="rounded-card border border-warning/30 bg-warning-bg px-4 py-3 text-sm text-warning">
          <strong>This booking's payment hold expired</strong> before the consumer completed payment - the
          trainer accepted, but the 15-minute (card) or longer (cash) window ran out. Reactivating restarts
          that window and reverts this session to <em>enquiry accepted</em>, awaiting payment again.
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.6fr_1fr]">
        {/* Main column */}
        <div className="space-y-6">
          <DetailCard title="Details">
            <div className="divide-y divide-hairline">
              <Fact label="Type" value={typeLabel(record.session_type)} />
              <Fact label="Status" value={<StatusBadge status={record.status} />} />
              {record.sport && <Fact label="Sport" value={<SportTag name={record.sport.name} />} />}
              {record.description && (
                <div className="py-2">
                  <div className="text-[13px] text-ink-2">Description</div>
                  <p className="mt-1 text-sm text-ink">{record.description}</p>
                </div>
              )}
            </div>
          </DetailCard>

          <DetailCard title="Schedule" icon={Calendar}>
            <div className="divide-y divide-hairline">
              <Fact
                label="Date & time"
                value={
                  record.scheduled_at ? (
                    <span>
                      {new Date(record.scheduled_at).toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' })}
                      {' · '}
                      {fmtTime(record.scheduled_at)}
                    </span>
                  ) : (
                    'Not scheduled'
                  )
                }
              />
              <Fact label="Duration" value={<span className="inline-flex items-center gap-1.5"><Clock className="size-3.5 text-ink-2" />{record.duration_minutes} min</span>} />
            </div>
          </DetailCard>

          <DetailCard title="Coach" icon={User}>
            {record.trainer ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Avatar src={record.trainer.profile_picture_url} name={record.trainer.name} size={44} />
                  <div>
                    <Link to={`/trainers/${record.trainer.id}/show`} className="font-semibold text-indigo hover:text-indigo-press">
                      {record.trainer.name}
                    </Link>
                    {record.trainer.email && <div className="text-[13px] text-ink-2">{record.trainer.email}</div>}
                  </div>
                </div>
                {record.trainer_notes && <p className="rounded-input bg-surface-2 px-3 py-2 text-sm text-ink">{record.trainer_notes}</p>}
              </div>
            ) : (
              <p className="text-sm text-ink-2">No coach assigned</p>
            )}
          </DetailCard>

          {record.session_type === 'private_session' && record.requester && (
            <DetailCard title="Requester" icon={User}>
              <div className="space-y-2">
                <Link to={`/users/${record.requester.id}/show`} className="font-semibold text-indigo hover:text-indigo-press">
                  {record.requester.name}
                </Link>
                {record.requester.email && (
                  <div className="flex items-center gap-2 text-sm text-ink"><Mail className="size-4 text-ink-2" />{record.requester.email}</div>
                )}
                {record.requester.phone && (
                  <div className="flex items-center gap-2 text-sm text-ink"><Phone className="size-4 text-ink-2" />{record.requester.phone}</div>
                )}
              </div>
            </DetailCard>
          )}

          {record.location && (
            <DetailCard title="Location" icon={MapPin}>
              <Link to={`/locations/${record.location.id}/show`} className="font-semibold text-indigo hover:text-indigo-press">
                {record.location.name}
              </Link>
              {record.location.address && <p className="mt-1 text-sm text-ink-2">{record.location.address}</p>}
            </DetailCard>
          )}

          <DetailCard title={`Roster (${record.current_participants || 0}${record.max_participants ? `/${record.max_participants}` : ''})`} icon={Users}>
            {record.participants && record.participants.length > 0 ? (
              <div className="space-y-2">
                {record.participants.map((p) => (
                  <div key={p.booking_id} className="flex items-center justify-between gap-3 rounded-input border border-hairline px-3 py-2">
                    <div className="flex items-center gap-3">
                      <Avatar name={p.user_name} size={32} />
                      <div className="min-w-0">
                        <Link to={`/users/${p.user_id}/show`} className="font-medium text-ink hover:text-indigo">
                          {p.user_name || 'Unknown'}
                        </Link>
                        {p.user_email && <div className="text-[13px] text-ink-2">{p.user_email}</div>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={p.booking_status} />
                      <Link to={`/bookings/${p.booking_id}/show`} className="text-[13px] font-semibold text-indigo hover:text-indigo-press">
                        Booking
                      </Link>
                      {canRemoveFromClass(record.session_type, p.booking_status) && (
                        <RemoveFromClassAction
                          bookingId={p.booking_id}
                          playerName={p.user_name}
                          sessionId={record.id}
                          variant="icon"
                          onRemoved={refresh}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="py-4 text-center text-sm text-ink-2">No participants yet</p>
            )}
          </DetailCard>

          <DetailCard title={`Chat (${messages.length})`} icon={MessageCircle}>
            <ScrollArea className="h-[420px] pr-2">
              {messagesLoading ? (
                <p className="py-8 text-center text-sm text-ink-2">Loading messages…</p>
              ) : messages.length > 0 ? (
                <div className="divide-y divide-hairline">
                  {messages.map((m) => (
                    <ChatMessageRow key={m.id} message={m} onDelete={(id) => setDeleteMessageId(id)} />
                  ))}
                </div>
              ) : (
                <p className="py-8 text-center text-sm text-ink-2">No messages in this class chat yet</p>
              )}
            </ScrollArea>
          </DetailCard>
        </div>

        {/* Aside */}
        <div className="space-y-6">
          {record.max_participants ? (
            <DetailCard title="Capacity">
              <Capacity filled={record.current_participants || 0} total={record.max_participants} />
              <div className="mt-3 divide-y divide-hairline">
                <Fact label="Available spots" value={Math.max(0, record.max_participants - (record.current_participants || 0))} />
              </div>
            </DetailCard>
          ) : null}

          <DetailCard title="Pricing">
            <div className="divide-y divide-hairline">
              {record.price_per_person !== undefined && record.price_per_person !== null && (
                <Fact label="Per person" value={<Money value={record.price_per_person} />} />
              )}
              <Fact label="Total revenue" value={<Money value={record.total_revenue} className="text-base font-bold" />} />
            </div>
          </DetailCard>

          {record.booking_stats && (
            <DetailCard title="Bookings">
              <div className="divide-y divide-hairline">
                <Fact label="Total" value={record.booking_stats.total_bookings || 0} />
                <Fact label="Confirmed" value={<span className="text-success">{record.booking_stats.active_bookings || 0}</span>} />
                <Fact label="Cancelled" value={<span className="text-error">{record.booking_stats.cancelled_bookings || 0}</span>} />
                <Fact label="Removed" value={<span className="text-error">{record.booking_stats.removed_bookings || 0}</span>} />
              </div>
            </DetailCard>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        tone="destructive"
        title={`Cancel class #${record.id}?`}
        consequence="This cancels the class and all active bookings. Participants are notified."
        confirmLabel="Cancel class"
        cancelLabel="Keep class"
        reasonRequired
        reasonChips={['Trainer unavailable', 'Weather conditions', 'Facility issue', 'Low enrollment']}
        onConfirm={handleCancel}
      />

      <ConfirmDialog
        open={reactivateOpen}
        onOpenChange={setReactivateOpen}
        tone="indigo"
        title={`Reactivate hold on session #${record.id}?`}
        consequence="Reverts the booking to enquiry_accepted / pending and restarts its payment window. Use this when the hold expired while a slower payment (e.g. cash) was still in progress."
        confirmLabel="Reactivate"
        cancelLabel="Cancel"
        onConfirm={handleReactivate}
      />

      <ConfirmDialog
        open={deleteMessageId !== null}
        onOpenChange={(o) => !o && setDeleteMessageId(null)}
        tone="destructive"
        title="Delete this message?"
        consequence="The message is removed from the class chat."
        confirmLabel="Delete message"
        reasonRequired
        reasonChips={['Inappropriate content', 'Spam', 'Harassment']}
        onConfirm={handleDeleteMessage}
      />
    </div>
  );
};

export const SessionShow = (props: any) => (
  <Show {...props} actions={false} title=" " component="div">
    <SessionShowContent />
  </Show>
);
